import { isObject } from 'ts-deep-utils'
import type { components, paths } from '../types/_api.js'
import type { hiddenPaths } from './_apiHidden.js'
import type { PathsWithMethod, SuccessResponseJSON, ErrorResponseJSON } from 'openapi-typescript-helpers'
import type { RequestBodyOption } from 'openapi-fetch'

export type allPaths = paths & hiddenPaths

// ---------------------------------------------------------------------------
// Pre-computed path unions per HTTP method (evaluated once, then cached by TS)
// ---------------------------------------------------------------------------
export type GetPaths = PathsWithMethod<allPaths, 'get'>
export type PostPaths = PathsWithMethod<allPaths, 'post'>
export type PutPaths = PathsWithMethod<allPaths, 'put'>
export type PatchPaths = PathsWithMethod<allPaths, 'patch'>
export type DeletePaths = PathsWithMethod<allPaths, 'delete'>
export type MutationMethod = 'post' | 'put' | 'patch' | 'delete'

// ---------------------------------------------------------------------------
// Pre-computed GET response data, error, and params (mapped-type lookup tables)
// ---------------------------------------------------------------------------
export type GetResponseData = {
  [P in GetPaths]: SuccessResponseJSON<Required<allPaths[P]['get']>>
}
export type GetResponseError = {
  [P in GetPaths]: ErrorResponseJSON<Required<allPaths[P]['get']>>
}
export type GetParams = {
  [P in GetPaths]: allPaths[P]['get'] extends { parameters: infer Params }
    ? Params
    : { query?: Record<string, unknown> }
}

// ---------------------------------------------------------------------------
// Pre-computed mutation response data, error, params, and body
// Nested by method so they can be indexed as MutationX[Method][Path]
// ---------------------------------------------------------------------------
interface _MutationResponseData {
  post: { [P in PostPaths]: SuccessResponseJSON<Required<allPaths[P]['post']>> }
  put: { [P in PutPaths]: SuccessResponseJSON<Required<allPaths[P]['put']>> }
  patch: { [P in PatchPaths]: SuccessResponseJSON<Required<allPaths[P]['patch']>> }
  delete: { [P in DeletePaths]: SuccessResponseJSON<Required<allPaths[P]['delete']>> }
}
interface _MutationResponseError {
  post: { [P in PostPaths]: ErrorResponseJSON<Required<allPaths[P]['post']>> }
  put: { [P in PutPaths]: ErrorResponseJSON<Required<allPaths[P]['put']>> }
  patch: { [P in PatchPaths]: ErrorResponseJSON<Required<allPaths[P]['patch']>> }
  delete: { [P in DeletePaths]: ErrorResponseJSON<Required<allPaths[P]['delete']>> }
}
interface _MutationParams {
  post: {
    [P in PostPaths]: allPaths[P]['post'] extends { parameters: infer Params }
      ? Params
      : { query?: Record<string, unknown> }
  }
  put: {
    [P in PutPaths]: allPaths[P]['put'] extends { parameters: infer Params }
      ? Params
      : { query?: Record<string, unknown> }
  }
  patch: {
    [P in PatchPaths]: allPaths[P]['patch'] extends { parameters: infer Params }
      ? Params
      : { query?: Record<string, unknown> }
  }
  delete: {
    [P in DeletePaths]: allPaths[P]['delete'] extends { parameters: infer Params }
      ? Params
      : { query?: Record<string, unknown> }
  }
}
interface _MutationBody {
  post: { [P in PostPaths]: RequestBodyOption<allPaths[P]['post']> }
  put: { [P in PutPaths]: RequestBodyOption<allPaths[P]['put']> }
  patch: { [P in PatchPaths]: RequestBodyOption<allPaths[P]['patch']> }
  delete: { [P in DeletePaths]: RequestBodyOption<allPaths[P]['delete']> }
}

export type MutationResponseData<
  M extends MutationMethod,
  P extends PathsWithMethod<allPaths, M>,
> = _MutationResponseData[M][P & keyof _MutationResponseData[M]]

export type MutationResponseError<
  M extends MutationMethod,
  P extends PathsWithMethod<allPaths, M>,
> = _MutationResponseError[M][P & keyof _MutationResponseError[M]]

export type MutationVariables<M extends MutationMethod, P extends PathsWithMethod<allPaths, M>> = _MutationParams[M][P &
  keyof _MutationParams[M]] &
  _MutationBody[M][P & keyof _MutationBody[M]]

type ExtractResourceRoot<T extends string> = T extends `/${string}/${string}`
  ? never
  : T extends `/${infer U}`
    ? U
    : never
type ExtractResourceChild<T extends string> = T extends `/${infer U}/{resource_id}`
  ? U extends `${string}/${string}`
    ? never
    : U
  : never
type ExtractResourceSearch<T extends string> = T extends `/search/${infer U}` ? U : never
export type Resource = ExtractResourceRoot<keyof allPaths> | ExtractResourceChild<keyof allPaths>
export type ItemResource = Exclude<ExtractResourceChild<keyof allPaths>, 'application--details'>
export type SearchResource = ExtractResourceSearch<keyof allPaths>

export type IdNumber = NonNullable<NonNullable<components['schemas']['Customer']['id_number']>['id_number_type']>

export interface MediaFileReference {
  id?: string
  filename?: string
  content_type?: string
  size?: number
}
export function isMediaFileReference(x: unknown): x is MediaFileReference {
  return isObject(x) && 'content_type' in x && 'filename' in x && 'size' in x
}

export interface Address {
  address_line_1: string
  address_line_2?: string
  address_line_3?: string
  zip_code: string
  city: string
  country: string
  gln?: string
}

export interface Item {
  _id: string
  _etag?: string
  [key: string]: unknown
}

export interface ListResponse<T extends Item> {
  _items: T[]
  _meta: components['schemas']['response_metadata']
}
export function isListResponse<T extends Item>(x: unknown): x is ListResponse<T> {
  return (
    x != null &&
    typeof x === 'object' &&
    Object.hasOwn(x, '_meta') &&
    Object.hasOwn(x, '_items') &&
    Array.isArray((x as { _items?: unknown[] })._items)
  )
}

export interface EveUpdateResponse {
  _id: string
  _etag: string
  _updated: string
  _status: 'OK'
}
export function isEveUpdateResponse(x: unknown): x is EveUpdateResponse {
  return x != null && typeof x === 'object' && '_status' in x && x._status === 'OK' && Object.hasOwn(x, '_id')
}

// Recursive type for nested error issues (allows arbitrarily nested object structures)
export interface IssuesRecord {
  [key: string]: string | string[] | IssuesRecord
}

export interface EveItemErrorResponseBody {
  _status: 'ERR'
  _error?: string | { code: number; message?: string }
  _issues?: IssuesRecord

  // ADA Extension:
  _locale_message?: string
}
export function isEveSingleErrorResponseBody(x: unknown): x is EveItemErrorResponseBody {
  return typeof x === 'object' && x !== null && '_status' in x && x._status === 'ERR' && !('_items' in x)
}

export interface EveListErrorResponseBody {
  _status: 'ERR'
  _error?: string | { code: number; message?: string }
  _items: (
    | {
        _status: 'OK'
      }
    | {
        _status: 'ERR'
        _issues?: IssuesRecord
      }
  )[]

  // ADA Extension:
  _locale_message?: string
}
export function isEveListErrorResponseBody(x: unknown): x is EveListErrorResponseBody {
  return typeof x === 'object' && x !== null && '_status' in x && x._status === 'ERR' && '_items' in x
}
